import {
    kAllAudiences,
    type AppDefinition,
    type Audience,
    type AudienceGated,
} from "@/client/core/Core.api";

/**
 * LaunchDarkly tier flags: WHO is in each audience is targeted in the LD
 * dashboard (internal targets the UUID-based Splice Internal segment); this
 * map is the single code-side source for the flag keys.
 */
export const kAudienceFlagKeys: Record<Audience, string> = {
    rnd: "labs.audience-rnd.rel.20260728",
    internal: "labs.audience-internal.rel.20260728",
    soundcheck: "labs.audience-soundcheck.rel.20260728",
    partner: "labs.audience-partner.rel.20260728",
};

export interface AudienceState {
    /** False until LD resolves (or times out / dev defaults kick in). */
    isReady: boolean;
    /**
     * Effective audiences: the resolved tiers, unless an rnd user (or a dev
     * build) is previewing another tier via the menu toggle or
     * `?forceAudience=`.
     */
    audiences: ReadonlySet<Audience>;
    /** The tiers LaunchDarkly actually resolved, ignoring any preview. */
    resolvedAudiences: ReadonlySet<Audience>;
    /** Raw preview value when one is active (e.g. "soundcheck", "none"). */
    forcedAudience?: string;
    /**
     * Raw boolean flags, for per-app `flagKey` overrides. Partial: absent
     * keys yield undefined.
     */
    flags: Readonly<Partial<Record<string, boolean>>>;
}

export const resolveAudiences = (
    flags: Readonly<Partial<Record<string, boolean>>>,
): ReadonlySet<Audience> =>
    new Set(kAllAudiences.filter((audience) => flags[kAudienceFlagKeys[audience]] === true));

/**
 * R&D users see everything gated this way; otherwise the declared tiers must
 * intersect the user's audiences, or the per-item override flag must be on.
 * (Internal is NOT a superset: rnd-only items stay hidden from internal until
 * promoted.)
 *
 * Takes the gating fields rather than a whole `AppDefinition`, because a model
 * release offered only inside a capability's picker has no app page to carry
 * them — and the rule is the same either way.
 */
export const isAppVisible = (
    app: Readonly<AudienceGated>,
    state: Readonly<AudienceState>,
): boolean => {
    if (state.audiences.has("rnd")) {
        return true;
    }
    if (app.audiences.some((audience) => state.audiences.has(audience))) {
        return true;
    }
    if (app.flagKey === undefined) {
        return false;
    }
    return state.flags[app.flagKey] === true;
};

export const getVisibleApps = (
    apps: readonly AppDefinition[],
    state: Readonly<AudienceState>,
): AppDefinition[] => apps.filter((app) => isAppVisible(app, state));

const isAudience = (value: string): value is Audience =>
    (kAllAudiences as readonly string[]).includes(value);

/**
 * Parses a `?forceAudience=` override: a comma list of audiences, "none"
 * (preview the no-access state), or "clear" (drop a persisted override —
 * callers handle persistence). Returns undefined when the param is absent
 * or contains no recognizable value.
 */
export const parseForcedAudiences = (
    search: string,
): ReadonlySet<Audience> | "none" | "clear" | undefined => {
    const raw = new URLSearchParams(search).get("forceAudience");
    if (raw === null || raw === "") {
        return undefined;
    }
    if (raw === "none") {
        return "none";
    }
    if (raw === "clear") {
        return "clear";
    }
    const audiences = raw
        .split(",")
        .map((part) => part.trim())
        .filter(isAudience);
    return audiences.length > 0 ? new Set(audiences) : undefined;
};
